import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, ListOrdered } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTheme } from '../../store/theme-context';

const EXT_TO_LANGUAGE: Record<string, string> = {
  js: 'javascript', mjs: 'javascript', cjs: 'javascript', jsx: 'jsx',
  ts: 'typescript', tsx: 'tsx',
  py: 'python', rb: 'ruby', go: 'go', rs: 'rust',
  java: 'java', kt: 'kotlin', swift: 'swift',
  c: 'c', h: 'c', cpp: 'cpp', cc: 'cpp', hpp: 'cpp',
  cs: 'csharp', php: 'php',
  sh: 'bash', bash: 'bash', zsh: 'bash',
  css: 'css', scss: 'scss', less: 'less',
  html: 'markup', htm: 'markup', xml: 'markup', svg: 'markup',
  yaml: 'yaml', yml: 'yaml', toml: 'toml',
  json: 'json',
  md: 'markdown', markdown: 'markdown',
  sql: 'sql', graphql: 'graphql', gql: 'graphql',
  dockerfile: 'docker',
};

export function languageFromExt(ext: string): string {
  return EXT_TO_LANGUAGE[ext.toLowerCase()] ?? 'text';
}

interface CodeContentProps {
  children: string;
  language?: string;
  label?: string;
  maxHeight?: string;
  showHeader?: boolean;
  showLineNumbers?: boolean;
  wrapLongLines?: boolean;
  className?: string;
}

export function CodeContent({
  children,
  language = 'text',
  label,
  maxHeight,
  showHeader = true,
  showLineNumbers = false,
  wrapLongLines = false,
  className = '',
}: CodeContentProps) {
  const { resolvedTheme } = useTheme();
  const [copied, setCopied] = useState(false);
  // `showLineNumbers` prop sets the initial state; the user can flip it
  // via the toggle button in the header. Local state survives parent
  // re-renders that pass the same prop value.
  const [lineNumbersOn, setLineNumbersOn] = useState(showLineNumbers);
  const theme = resolvedTheme === 'dark' ? nord : oneLight;

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const headerLabel = label ?? (language === 'text' ? 'TEXT' : language.toUpperCase());

  return (
    <div className={`overflow-hidden rounded border border-border ${className}`}>
      {showHeader && (
        <div className="flex items-center justify-between border-b border-border bg-muted px-3 py-1.5">
          <span className="text-xs font-mono text-muted-foreground">{headerLabel}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setLineNumbersOn((v) => !v)}
              aria-pressed={lineNumbersOn}
              aria-label={lineNumbersOn ? 'Hide line numbers' : 'Show line numbers'}
              title={lineNumbersOn ? 'Hide line numbers' : 'Show line numbers'}
              className={cn(
                'focus-ring inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs hover:bg-accent hover:text-foreground',
                lineNumbersOn ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              <ListOrdered className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={copyToClipboard}
              aria-label={copied ? 'Copied' : 'Copy to clipboard'}
              title={copied ? 'Copied' : 'Copy to clipboard'}
              className="focus-ring inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {copied ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
      )}
      <div className="overflow-auto" style={maxHeight ? { maxHeight } : undefined}>
        <SyntaxHighlighter
          language={language}
          style={theme}
          showLineNumbers={lineNumbersOn}
          wrapLongLines={wrapLongLines}
          customStyle={{
            margin: 0,
            padding: '0.75rem',
            background: 'hsl(var(--card))',
            fontSize: '0.75rem',
            lineHeight: '1.5',
          }}
          codeTagProps={{
            style: { background: 'transparent', textShadow: 'none' },
          }}
          lineNumberStyle={{
            color: 'hsl(var(--muted-foreground))',
            paddingRight: '1rem',
            userSelect: 'none',
            background: 'transparent',
          }}
        >
          {children}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
